"use client";

import { getPlanByProductId } from "@gosaas/types/stripe";
import { IconChevronDown, IconPlus } from "@tabler/icons-react";
import { useState } from "react";
import { CreateTeamDialog } from "@/components/dialogs/create-team";
import { useTeam } from "@/components/providers/team";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuGroup,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
	SidebarMenu,
	SidebarMenuItem,
	useSidebar,
} from "@/components/ui/sidebar";
import { isModuleEnabled } from "@/lib/modules";
import { cn } from "@/lib/utils";

export function TeamSwitcher() {
	const { teams, teamID, setTeam } = useTeam();
	const { isMobile } = useSidebar();
	const [showCreateTeam, setShowCreateTeam] = useState(false);
	const [dropdownOpen, setDropdownOpen] = useState(false);

	const activeTeam = teamID
		? teams.find((team) => team.id === teamID)
		: teams[0];

	const teamEmoji = activeTeam?.emoji
		? decodeURIComponent(activeTeam.emoji)
		: "🔍";
	const teamName = activeTeam?.name ?? "";
	const planName = activeTeam?.billing
		? getPlanByProductId(activeTeam.billing.stripe_product_id)?.name
		: undefined;

	if (!isModuleEnabled("team")) {
		return (
			<SidebarMenu>
				<SidebarMenuItem>
					<div className="flex w-[200px] group-data-[collapsible=icon]:w-auto group-data-[collapsible=icon]:mx-auto items-center gap-2 rounded-md p-2 h-12">
						<div className="flex aspect-square size-8 group-data-[collapsible=icon]:size-full items-center justify-center rounded-lg bg-sidebar-primary text-sidebar-primary-foreground text-xl shrink-0">
							{teamEmoji}
						</div>
						<div className="grid flex-1 text-left text-sm leading-tight group-data-[collapsible=icon]:hidden">
							<span className="truncate font-semibold">{teamName}</span>
							{planName && <span className="truncate text-xs">{planName}</span>}
						</div>
					</div>
				</SidebarMenuItem>
			</SidebarMenu>
		);
	}

	return (
		<SidebarMenu>
			<SidebarMenuItem>
				<DropdownMenu open={dropdownOpen} onOpenChange={setDropdownOpen}>
					<DropdownMenuTrigger className="ring-sidebar-ring hover:bg-sidebar-accent hover:text-sidebar-accent-foreground active:bg-sidebar-accent active:text-sidebar-accent-foreground data-active:bg-sidebar-accent data-active:text-sidebar-accent-foreground data-open:hover:bg-sidebar-accent data-open:hover:text-sidebar-accent-foreground gap-2 rounded-md p-2 text-left text-sm transition-[width,height,padding] group-has-data-[sidebar=menu-action]/menu-item:pr-8 group-data-[collapsible=icon]:size-8! group-data-[collapsible=icon]:p-2! focus-visible:ring-2 data-active:font-medium peer/menu-button flex w-[200px] group-data-[collapsible=icon]:w-auto group-data-[collapsible=icon]:mx-auto items-center overflow-hidden outline-hidden group/menu-button disabled:pointer-events-none disabled:opacity-50 aria-disabled:pointer-events-none aria-disabled:opacity-50 [&>span:last-child]:truncate [&_svg]:size-4 [&_svg]:shrink-0 h-12 text-sm group-data-[collapsible=icon]:p-0! group-data-[collapsible=icon]:overflow-visible! group-data-[collapsible=icon]:bg-transparent! group-data-[collapsible=icon]:hover:bg-transparent! data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground">
						<div className="flex aspect-square size-8 group-data-[collapsible=icon]:size-full items-center justify-center rounded-lg bg-sidebar-primary text-sidebar-primary-foreground text-xl shrink-0">
							{teamEmoji}
						</div>
						<div className="grid flex-1 text-left text-sm leading-tight group-data-[collapsible=icon]:hidden">
							<span className="truncate font-semibold">{teamName}</span>
							{planName && <span className="truncate text-xs">{planName}</span>}
						</div>
						<IconChevronDown
							className={cn(
								"ml-auto group-data-[collapsible=icon]:hidden transition-transform duration-500 ease-in-out",
								dropdownOpen && "rotate-270",
							)}
						/>
					</DropdownMenuTrigger>
					<DropdownMenuContent
						className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
						align="start"
						side={isMobile ? "bottom" : "right"}
						sideOffset={4}
					>
						<DropdownMenuGroup>
							<DropdownMenuLabel className="text-xs text-muted-foreground">
								Teams
							</DropdownMenuLabel>
							{teams.map((team) => (
								<DropdownMenuItem
									key={team.id}
									onClick={() => {
										setTeam(team.id);
									}}
									className="gap-2 p-2"
								>
									<div className="flex size-6 items-center justify-center bg-muted rounded-sm border">
										{team?.emoji ? decodeURIComponent(team.emoji) : "🔍"}
									</div>
									{team.name}
								</DropdownMenuItem>
							))}
						</DropdownMenuGroup>
						<DropdownMenuSeparator />
						<DropdownMenuItem
							className="gap-2 p-2"
							onClick={(event) => {
								event.preventDefault();
								setDropdownOpen(false);
								setShowCreateTeam(true);
							}}
						>
							<div className="flex size-6 items-center justify-center rounded-md border bg-background">
								<IconPlus className="size-4" />
							</div>
							<div className="font-medium text-muted-foreground">Add team</div>
						</DropdownMenuItem>
					</DropdownMenuContent>
				</DropdownMenu>
			</SidebarMenuItem>
			<CreateTeamDialog
				open={showCreateTeam}
				onOpenChange={setShowCreateTeam}
			/>
		</SidebarMenu>
	);
}
